import axios from "axios";
import React, { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";

interface IProps {
  onError: (message: React.ReactNode) => void;
  onSuccess: (message: React.ReactNode) => void;
  isRegister: boolean;
  setIsRegister: () => void;
  setIsShowLogin: () => void;
}

const schema = yup.object().shape({
  username: yup.string().required("Tên tài khoản bắt buộc"),
  managedBy: yup.string().required("Đại lý bắt buộc"),
  phone_number: yup
    .string()
    .matches(/^\d{10}$/, "Số điện thoại phải có 10 chữ số")
    .required("Số điện thoại bắt buộc"),
  password: yup.string().min(6, "Mật khẩu ít nhất 6 ký tự").required(),
});

const Register: React.FC<IProps> = ({
  onError,
  onSuccess,
  isRegister,
  setIsRegister,
  setIsShowLogin,
}) => {
  const [showPassword, setShowPassword] = useState(false);

  //
  const inputClassName =
    "w-full rounded-lg border-[0.69px] border-[#0072A3] bg-[#FFFFFF] md:px-3 px-1.5 md:py-2.5 py-1.5 md:mb-3 mb-1.5  font-mono text-lg font-medium text-[#0072A3] outline-none shadow-[inset_0px_-4px_8.4px_0px_#00AAFFD9] placeholder:text-[#0072A3]";

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleRegister = async (data: any) => {
    try {
      await axios
        .post(`${process.env.REACT_APP_URL_API}/auth/register`, {
          username: data.username,
          managedByUsername: data.managedBy,
          phone: data.phone_number,
          password: String(data.password),
        })
        .then((data) => {
          if (data.status === 201) {
            setIsRegister();
            onSuccess(
              <>
                CHÚC MỪNG ĐĂNG KÝ THÀNH CÔNG!
                <br />
                VUI LÒNG ĐỢI ÍT PHÚT.
              </>,
            );
            reset();
          }
        })
        .catch((err) => {
          if (
            err.response.data.message[0] === "managedBy must be a mongodb id"
          ) {
            onError("Thông tin đại lý chưa chính xác!");
          } else {
            onError(`${err.response.data.message}`);
          }
        });
    } catch (error) {
      return error;
    }
  };

  return (
    <div className="fixed w-full h-full top-0 left-0 flex flex-col items-center justify-center bg-black/60">
      <img
        className="z-20"
        src="/assets/common/text-register.png"
        alt="ĐĂNG Ký"
      />

      <div className="md:w-[630px] md:h-[480px] w-[425px] h-[380px] relative flex flex-col items-center justify-center ">
        {/* overlay */}
        <img
          className="absolute top-0 left-0 z-10 "
          src="/assets/common/form-bg.png"
          alt="background"
        />

        <form
          className="absolute md:top-1/2 top-[45%] left-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-[80%] flex flex-col items-center"
          id="regisform"
          onSubmit={handleSubmit(handleRegister)}
        >
          {/* Tài khoản */}

          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <input
                {...field}
                placeholder="Nhập tên đăng nhập"
                maxLength={16}
                autoComplete="username"
                className={inputClassName}
              />
            )}
          />
          {errors.username?.message ? (
            <div className="my-1 text-xl text-red-900">
              {errors.username.message}
            </div>
          ) : null}

          {/* Số điện thoại */}
          <>
            <Controller
              name="phone_number"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  placeholder="Nhập số điện thoại"
                  maxLength={10}
                  className={inputClassName}
                />
              )}
            />
            {errors.phone_number?.message ? (
              <div className="my-1 text-xl text-red-900">
                {errors.phone_number.message}
              </div>
            ) : null}
          </>

          {/* Mật khẩu */}
          <>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <div className="w-full relative flex items-center">
                  <input
                    {...field}
                    type={showPassword ? "text" : "password"}
                    placeholder="Nhập mật khẩu"
                    maxLength={16}
                    autoComplete="new-password"
                    className={inputClassName}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2.5 top-[42%] -translate-y-1/2 p-1 text-slate-500"
                    aria-label={showPassword ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      {showPassword ? (
                        <>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </>
                      ) : (
                        <>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </>
                      )}
                    </svg>
                  </button>
                </div>
              )}
            />
            {errors.password?.message ? (
              <div className="my-1 text-xl text-red-900">
                {errors.password.message}
              </div>
            ) : null}
          </>

          {/* Mã giới thiệu */}
          <>
            <Controller
              name="managedBy"
              control={control}
              render={({ field }) => (
                <input
                  {...field}
                  placeholder="Nhập mã giới thiệu"
                  className={inputClassName}
                />
              )}
            />
            {errors.managedBy?.message ? (
              <div className="my-1 text-xl text-red-900">
                {errors.managedBy.message}
              </div>
            ) : null}
          </>

          {/* Nút Đăng ký */}
          <div className="flex items-center justify-center md:gap-2 gap-0 md:mt-8 mt-1">
            <button className="flex items-center justify-center" type="submit">
              <img
                className="md:w-full w-[90%]"
                src="/assets/common/btn-accept.png"
                alt="Đăng ký"
              />
            </button>
            <button
              className="flex items-center justify-center"
              type="button"
              onClick={setIsRegister}
            >
              <img
                className="md:w-full w-[90%]"
                src="/assets/common/btn-cancel.png"
                alt="Đóng modal"
              />
            </button>
          </div>
          <div className="text-center md:mt-3 mt-0">
            <span className="md:text-xl text-base font-bold text-white/90 mr-1">
              Bạn đã có tài khoản?
            </span>
            <span
              role="button"
              tabIndex={0}
              onClick={() => {
                setIsRegister();
                setIsShowLogin();
              }}
              onKeyDown={(e) =>
                e.key === "Enter" && (setIsRegister(), setIsShowLogin())
              }
              className="cursor-pointer md:text-xl text-base font-bold text-yellow-400 underline"
            >
              Đăng nhập ngay!
            </span>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
